import React, { useState } from "react";
import { AlertTriangle, Building2, FileText, ReceiptText, Settings as SettingsIcon, ShieldCheck, Trash2, X } from "lucide-react";
import PremiumHubPage from "../../../Components/Dashboard/PremiumHubPage";
import { BASE_URL } from "../CommonApi";
import {
  getActiveOrganization,
  getOrganizationIdentity,
  getStoredOrganizations,
  setActiveOrganization,
  setOrganizations,
} from "../../../utils/organizationContext";
import { hardRefreshPage } from "../../../utils/hardRefresh";

const Setting = ({ navigation }) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const go = (route) => () => navigation.navigate(route);

  const getActiveOrganizationDetails = () => {
    const activeOrganization = getActiveOrganization();
    const organizations = getStoredOrganizations();
    const organizationName =
      activeOrganization?.organizationName ||
      activeOrganization?.companyName ||
      activeOrganization?.organizationKey ||
      "this organization";
    return { activeOrganization, organizations, organizationName };
  };

  const openDeleteModal = () => {
    setDeleteConfirmation("");
    setDeleteError("");
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleteLoading) return;
    setDeleteModalOpen(false);
    setDeleteConfirmation("");
    setDeleteError("");
  };

  const deleteOrganization = async (event) => {
    event.preventDefault();
    const { activeOrganization, organizations, organizationName } = getActiveOrganizationDetails();
    const organizationId = activeOrganization?.id;

    if (!organizationId) {
      setDeleteError("Please select an organization before deleting.");
      return;
    }

    if (organizations.length <= 1) {
      setDeleteError("You cannot delete your only organization. Create or switch to another organization first.");
      return;
    }

    if (deleteConfirmation.trim() !== organizationName) {
      setDeleteError("Organization name did not match. Nothing was deleted.");
      return;
    }

    setDeleteLoading(true);
    setDeleteError("");
    try {
      const response = await fetch(`${BASE_URL}/organizations/${organizationId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwtToken")}`,
        },
      });
      const data = await response.json();
      if (!response.ok || data.status !== "success") {
        throw new Error(data.responseMessage || "Unable to delete organization");
      }

      const nextOrganizations = organizations.filter(
        (organization) => getOrganizationIdentity(organization) !== getOrganizationIdentity(activeOrganization)
      );
      setOrganizations(nextOrganizations, { silent: true });
      setActiveOrganization(nextOrganizations[0] || null, { silent: true });
      setDeleteModalOpen(false);
      hardRefreshPage();
    } catch (error) {
      setDeleteError(error.message || "Unable to delete organization. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };
  const { organizationName } = getActiveOrganizationDetails();

  return (
    <>
      <PremiumHubPage
        title="Settings"
        subtitle="Configure your organization, taxes, templates, billing preferences, and access controls."
        sections={[
          {
            title: "Organization",
            items: [
              { label: "Organization Profile", description: "Business details, address, currency, and location.", icon: Building2, tone: "blue", onClick: go("profile") },
              { label: "Users & Roles", description: "Invite team members and control workspace access.", icon: ShieldCheck, tone: "green", onClick: go("Users") },
            ],
          },
          {
            title: "Sales & Tax",
            items: [
              { label: "Taxes", description: "GST rates, tax preferences, and compliance setup.", icon: ReceiptText, tone: "orange", onClick: go("taxes") },
              { label: "Templates", description: "Invoice, quotation, and document templates.", icon: FileText, tone: "blue", onClick: go("templates") },
            ],
          },
          {
            title: "Preferences",
            items: [
              { label: "Document Column Preferences", description: "Discount and HSN/SAC defaults across all documents.", icon: SettingsIcon, tone: "green", onClick: go("preferences") },
            ],
          },
          {
            title: "Danger Zone",
            items: [
              { label: "Delete Organization", description: "Permanently delete this organization and all related business data.", icon: Trash2, tone: "red", onClick: openDeleteModal },
            ],
          },
        ]}
      />

      {deleteModalOpen && (
        <div className="zarp-modal-backdrop" role="presentation">
          <section className="zarp-modal zarp-danger-modal" role="dialog" aria-modal="true" aria-labelledby="delete-org-title">
            <button
              type="button"
              className="zarp-modal-close"
              aria-label="Close"
              onClick={closeDeleteModal}
              disabled={deleteLoading}
            >
              <X size={18} />
            </button>
            <div className="zarp-modal-head">
              <span className="zarp-modal-icon red">
                <AlertTriangle size={22} />
              </span>
              <div>
                <h2 id="delete-org-title">Delete Organization</h2>
                <p>This permanently removes the organization and all related business data. Your user account remains active.</p>
              </div>
            </div>

            <form className="zarp-modal-form" onSubmit={deleteOrganization}>
              <div className="zarp-delete-summary">
                <span>Selected organization</span>
                <strong>{organizationName}</strong>
              </div>
              <label>
                <span>Type organization name to confirm</span>
                <input
                  autoFocus
                  value={deleteConfirmation}
                  onChange={(event) => setDeleteConfirmation(event.target.value)}
                  placeholder={organizationName}
                />
              </label>
              {deleteError && <div className="zarp-modal-error">{deleteError}</div>}
              <div className="zarp-modal-actions">
                <button type="button" className="zarp-modal-secondary" onClick={closeDeleteModal} disabled={deleteLoading}>
                  Cancel
                </button>
                <button type="submit" className="zarp-modal-danger" disabled={deleteLoading}>
                  {deleteLoading ? "Deleting..." : "Delete Organization"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </>
  );
};

export default Setting;
